import type {
  CategoricalFilter,
  Dashboard,
  DataSource,
  DataTable,
  Extensions,
  Filter,
  HierarchicalFilter,
  RangeFilter,
  RelativeDateFilter,
} from "@tableau/extensions-api-types";
import { Workbook } from "exceljs";
import * as ecsAPI from "./plugin_step";

const IS_POST_API_ENABLED =
  (process && process.env && process.env.IS_POST_API_ENABLED) === "true";

document.addEventListener("DOMContentLoaded", async () => {
  const dashboard = await initDashboard(tableau.extensions);

  const worksheetSelect = initWorksheetSelect(dashboard);
  const worksheet = findWorksheetByName(dashboard, worksheetSelect.value);
  if (!worksheet) {
    updateInfoDiv({ errorMsg: `Worksheet "${worksheet}" not found.` });
    return;
  }

  const filtersDiv = document.getElementById("filters")!;
  updateFilterDiv(filtersDiv, dashboard);
  worksheet.addEventListener(tableau.TableauEventType.FilterChanged, () =>
    updateFilterDiv(filtersDiv, dashboard),
  );

  const exportButton = document.getElementById("exportButton")!;
  exportButton.addEventListener("click", async () => {
    try {
      updateInfoDiv({ showLoading: true });

      const dashboardFilters = await dashboard.getFiltersAsync();
      const filteredData = await worksheet.getSummaryDataAsync();
      const dataSources = await worksheet.getDataSourcesAsync();

      if (IS_POST_API_ENABLED) await ecsAPI.postFilterData(dashboardFilters);

      const excelData = createExcelData(
        dashboardFilters,
        filteredData,
        dataSources,
      );

      if (!excelData.length) {
        return updateInfoDiv({
          errorMsg:
            "No data to export for the selected worksheet with current filters.",
        });
      }

      const blob = await createExcelBlob(worksheet.name, ...excelData);

      downloadFile(
        `${worksheet.name}_filtered_data.xlsx`,
        blob,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      );

      updateInfoDiv({ showLoading: false });
    } catch (error) {
      console.error("Error exporting data:", error);
      updateInfoDiv({ errorMsg: "An error occurred during export: " + error });
    }
  });
});

type ExcelTable = {
  headers: string[];
  rows: string[][];
  type?: "asExcelTable";
};

function createExcelData(
  dashboardFilters: Filter[],
  filteredData: DataTable,
  dataSources: DataSource[],
) {
  const dataTable = parseDataTable(filteredData);

  const metaData1: ExcelTable = {
    headers: ["Data Source Extract Update Time:"],
    rows: dataSources
      .map((dataSource) => {
        let str = "Not updated since initial load.";
        if (dataSource.extractUpdateTime) {
          str = new Date(dataSource.extractUpdateTime + " GMT").toLocaleString(
            undefined,
            {
              dateStyle: "full",
              timeStyle: "long",
            },
          );
        }
        return [`    ${dataSource.name} → ${str}`];
      })
      .concat([[]]), // add a whitespace row
  };

  const metaData2: ExcelTable = {
    headers: ["Excel File Creation Time:"],
    rows: [
      [
        "    " +
          new Date().toLocaleString(undefined, {
            dateStyle: "full",
            timeStyle: "long",
          }),
      ],
      [],
    ],
  };

  const filterSummaries = parseFilterSummaries(dashboardFilters);
  const filters: ExcelTable = {
    headers: ["Results Filtered By:"],
    rows: filterSummaries.map(({ name, values }) => [
      `    ${name} → ${values.join(", ")}`,
    ]),
  };

  return [metaData1, metaData2, filters, dataTable];
}

async function createExcelBlob(sheetName: string, ...tables: ExcelTable[]) {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(sheetName, {
    properties: { defaultColWidth: 40 },
  });

  tables.forEach((table) => {
    if (table.type === "asExcelTable") {
      const headerRow = worksheet.addRow(table.headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "9C6500" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEB9C" },
        };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    } else {
      const headerRow = worksheet.addRow(table.headers);
      headerRow.font = { bold: true };
    }

    worksheet.addRows(table.rows);
    worksheet.addRow([""]);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

async function initDashboard(extensions: Extensions) {
  await extensions.initializeAsync();

  return extensions.dashboardContent?.dashboard!;
}

function findDashboardWorksheet(dashboard: Dashboard) {
  const dashboardObj = dashboard.getDashboardObjectById(
    dashboard.activeDashboardObjectId,
  );

  return dashboardObj?.worksheet!;
}

function findWorksheetByName(dashboard: Dashboard, sheetName: string) {
  return dashboard.worksheets.find((sheet) => sheet.name === sheetName);
}

function initWorksheetSelect(dashboard: Dashboard) {
  const worksheetSelect = document.getElementById(
    "worksheetSelect",
  )! as HTMLSelectElement;

  dashboard.worksheets.forEach((worksheet) => {
    const option = document.createElement("option");
    option.value = worksheet.name;
    option.innerText = worksheet.name;
    worksheetSelect.appendChild(option);
  });

  return worksheetSelect;
}

async function updateFilterDiv(filtersDiv: HTMLElement, dashboard: Dashboard) {
  const filters = await dashboard.getFiltersAsync();
  const filterSummaries = parseFilterSummaries(filters);

  filtersDiv.innerHTML = filterSummaries
    .map(({ name, values }) => `${name} → ${values.join(", ")}`)
    .join("<br/>");

  return filtersDiv;
}

function parseFilterSummaries(filters: Filter[]) {
  const summaries = filters
    .map((filter) => ({
      name: filter.fieldName,
      values: parseFilterValues(filter),
    }))
    .sort(({ name: a }, { name: b }) => a.localeCompare(b));

  return summaries;
}

function parseFilterValues(filter: Filter) {
  if ("isAllSelected" in filter && filter.isAllSelected) {
    return ["All"];
  }

  if (isCategoricalFilter(filter)) {
    return filter.appliedValues
      .map((val) => val.formattedValue || (val.value as string))
      .sort((a, b) => a.localeCompare(b));
  }

  if (isRangeFilter(filter)) {
    let value = "";

    value += filter.minValue ? filter.minValue.formattedValue : "";
    value += filter.minValue && filter.maxValue ? ` ⇔ ` : "";
    value += filter.maxValue ? filter.maxValue.formattedValue : "";

    return [value];
  }

  if (isRelativeDateFilter(filter)) {
    return [
      "Period: " + filter.periodType,
      "RangeN: " + filter.rangeN,
      "Range Type: " + filter.rangeType,
    ];
  }

  return [];
}

function parseDataTable(dataTable: DataTable): ExcelTable {
  if (!dataTable || dataTable.data.length === 0) {
    return { headers: [], rows: [], type: "asExcelTable" };
  }

  const headers = dataTable.columns.map((col) => col.fieldName);

  const rows = dataTable.data.map((row) =>
    row.map((cell) => (cell.value === null ? "" : (cell.value as string))),
  );

  return { headers, rows, type: "asExcelTable" };
}

function isCategoricalFilter(filter: Filter): filter is CategoricalFilter {
  return filter.filterType === "categorical";
}

function isRangeFilter(filter: Filter): filter is RangeFilter {
  return filter.filterType === "range";
}

function isHierarchicalFilter(filter: Filter): filter is HierarchicalFilter {
  return filter.filterType === "hierarchical";
}

function isRelativeDateFilter(filter: Filter): filter is RelativeDateFilter {
  return filter.filterType === "relative-date";
}

function updateInfoDiv(
  config: { errorMsg: string } | { showLoading: boolean },
) {
  const errorDiv = document.getElementById("error")!;
  errorDiv.innerText = "errorMsg" in config ? config.errorMsg : "";

  const loadingDiv = document.getElementById("loading")!;
  if ("showLoading" in config && config.showLoading) {
    loadingDiv.style.display = "block";
  } else {
    loadingDiv.style.display = "none";
  }
}

function downloadFile(name: string, data: BlobPart, type: string) {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = name;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
